import { Flex, Table, Tbody, Td, Th, Thead, Tr } from "@chakra-ui/react"
import { MappingResultRow, NO_SECTION } from "component/mapping/result/MappingResultRow"
import {
  SMRResultsFilters,
  SmrFilterOption,
  SmrStatusFilter,
} from "component/mapping/result/SMRResultsFilters"
import { stickyHeaderCellProps } from "component/table/stickyHeader"
import { FC, useCallback, useMemo, useRef, useState } from "react"
import { SmrMetadatas, UTDMetadatas } from "type/mapping/iterationMetadatas"
import { CorrectedResult, MappingResult } from "type/mapping/result"
import { WithId } from "type/withId"
import {
  hasMappedNomenclature,
  isSameAsMappedNomenclature,
} from "util/validation/mappedNomenclature"

interface MappingResultsTableProps {
  metadatas?: UTDMetadatas
  results: WithId<MappingResult>[]
  correctedResults: CorrectedResult[]
  onCorrectNomenclatureSelect: (result: CorrectedResult) => void
  isUTDIteration?: boolean
  isSMRIteration?: boolean
  smrMetadatas?: SmrMetadatas
  isIterationApproved?: boolean
  isLoading?: boolean
  // Фильтр статуса снаружи — страница переключает его после "Отметить все"
  status?: SmrStatusFilter
  onStatusChange?: (status: SmrStatusFilter) => void
}

const COLUMNS_NAME = [
  "Номенклатура Поставщика",
  "Сопоставленная Номенклатура из НСИ",
  "Сопоставленная Группа из НСИ",
  "Корректно / Некорректно",
  "Корректная Номенклатура",
  "Объяснение почему неверно сопоставили",
]

type Column = {
  title: string
  // Ширина колонки сметы (table-layout: fixed); без ширины — колонка
  // забирает всё оставшееся место
  width?: string
  // Вспомогательная колонка: ширина по содержимому, место отдаём названиям
  isCompact?: boolean
  isNumeric?: boolean
}

// Смета: вместо "номенклатуры" — "материал" или "работа", по выбранному типу
type SmrItemWords = {
  fromSmr: string
  mapped: string
  correct: string
  // Подсказка к "Скрыть совпадающие с НСИ"
  sameAsNsiHint: string
}

const SMR_ITEM_WORDS: Record<string, SmrItemWords> = {
  Материалы: {
    fromSmr: "Материал из сметы",
    mapped: "Сопоставленный материал из НСИ",
    correct: "Корректный материал",
    sameAsNsiHint:
      "Скрывает строки, где материал в смете совпадает с сопоставленным материалом из НСИ",
  },
  Работы: {
    fromSmr: "Работа из сметы",
    mapped: "Сопоставленная работа из НСИ",
    correct: "Корректная работа",
    sameAsNsiHint:
      "Скрывает строки, где работа в смете совпадает с сопоставленной работой из НСИ",
  },
}

const DEFAULT_SMR_ITEM_WORDS: SmrItemWords = {
  fromSmr: "Номенклатура из сметы",
  mapped: COLUMNS_NAME[1],
  correct: COLUMNS_NAME[4],
  sameAsNsiHint:
    "Скрывает строки, где номенклатура в смете совпадает с сопоставленной " +
    "номенклатурой из НСИ",
}

const getSmrItemWords = (typeLabel?: string) =>
  SMR_ITEM_WORDS[typeLabel || ""] || DEFAULT_SMR_ITEM_WORDS

// Ширины колонок проверки (смета и УПД) — от ширины таблицы (меняется при
// сворачивании сайдбара и размере окна). Названия и комментарий — главные, их держим; под
// ширину подстраиваются кол-во, цена и сумма. Считаем в JS: calc() с % в
// ячейках таблицы браузер не понимает
const REVIEW_WIDTHS = {
  number: 72,
  check: 100,
  feedback: 240,
  name: { min: 120, max: 280 },
  // Кол-во : цена : сумма — сумма длиннее всех ("6 831 957,86")
  amounts: { min: 270, max: 300, ratio: [80, 100, 110] },
}

const px = (width: number) => `${Math.floor(width)}px`

// Сколько ждать, пока ширина перестанет меняться (анимация сайдбара 0,2 с)
const RESIZE_SETTLE_MS = 150

// numberWidth — колонка "№ в смете", у УПД её нет
const getReviewWidths = (tableWidth: number, numberWidth: number) => {
  const { check, feedback, name, amounts } = REVIEW_WIDTHS
  const free = tableWidth - numberWidth - check - feedback
  // Названия поровну, пока кол-ву/цене/сумме хватает минимума
  const nameWidth = Math.max(name.min, Math.min(name.max, (free - amounts.min) / 3))
  const left = free - nameWidth * 3
  // Кол-во/цена/сумма забирают остаток до своего максимума. Что сверх —
  // поровну названиям и комментарию: названиям три четверти, а не всё
  // комментарию — на широком экране он раздувался, а названия переносились
  const amountsWidth = Math.max(amounts.min, Math.min(amounts.max, left))
  const extra = Math.max(0, left - amountsWidth) / 4
  const ratioSum = amounts.ratio.reduce((sum, part) => sum + part, 0)
  return {
    name: px(nameWidth + extra),
    feedback: px(feedback + extra),
    amounts: amounts.ratio.map((part) => px((amountsWidth * part) / ratioSum)),
  }
}

// Пока ширина таблицы не измерена — стартовые ширины
const getWidths = (tableWidth: number, numberWidth: number) =>
  tableWidth
    ? getReviewWidths(tableWidth, numberWidth)
    : { name: undefined, feedback: px(REVIEW_WIDTHS.feedback), amounts: ["75px", "93px", "102px"] }

const getSmrColumns = (typeLabel: string | undefined, tableWidth: number): Column[] => {
  const widths = getWidths(tableWidth, REVIEW_WIDTHS.number)
  const words = getSmrItemWords(typeLabel)
  return [
    // Номера в смете бывают четырёхзначные — не переносим
    { title: "№ в смете", isCompact: true, width: px(REVIEW_WIDTHS.number) },
    { title: words.fromSmr, width: widths.name },
    { title: words.mapped, width: widths.name },
    { title: COLUMNS_NAME[3], isCompact: true, width: px(REVIEW_WIDTHS.check) },
    // Сюда сметчик выбирает материал — та же ширина, что у названий, чтобы
    // выбранное название не ломало таблицу
    { title: words.correct, width: widths.name },
    { title: COLUMNS_NAME[5], width: widths.feedback },
    { title: "Кол-во", isCompact: true, isNumeric: true, width: widths.amounts[0] },
    { title: "Цена за ед.", isCompact: true, isNumeric: true, width: widths.amounts[1] },
    { title: "Сумма", isCompact: true, isNumeric: true, width: widths.amounts[2] },
  ]
}

// УПД — те же колонки, что у сметы, без номера позиции; группа НСИ подписью
// под сопоставленной номенклатурой, а не своей колонкой
const getUtdColumns = (tableWidth: number): Column[] => {
  const widths = getWidths(tableWidth, 0)
  return [
    { title: "Номенклатура поставщика", width: widths.name },
    { title: "Сопоставленная номенклатура из НСИ", width: widths.name },
    { title: COLUMNS_NAME[3], isCompact: true, width: px(REVIEW_WIDTHS.check) },
    { title: "Корректная номенклатура", width: widths.name },
    { title: COLUMNS_NAME[5], width: widths.feedback },
    { title: "Кол-во", isCompact: true, isNumeric: true, width: widths.amounts[0] },
    { title: "Цена за ед.", isCompact: true, isNumeric: true, width: widths.amounts[1] },
    { title: "Сумма с НДС", isCompact: true, isNumeric: true, width: widths.amounts[2] },
  ]
}

const ALL_SECTIONS = "__all__"
// Работы пока не сопоставляем — показываем, что они будут
const SOON_TYPES = ["Работы"]

// Тип позиции в BI ("материал(топ-30)", позже работы) → название вкладки
const getTypeLabel = (type?: string) => {
  const lowerType = type?.toLowerCase() || ""
  if (lowerType.includes("материал")) return "Материалы"
  if (lowerType.includes("работ")) return "Работы"
  return type || "Без типа"
}

// Значения и сколько раз каждое встретилось — в порядке первого появления.
// isCounted — какие строки считать: значение остаётся в списке и с нулём,
// иначе со скрытыми строками пропадали бы тип или раздел
const countBy = <T,>(
  items: T[],
  getKey: (item: T) => string,
  isCounted: (item: T) => boolean = () => true,
): SmrFilterOption[] => {
  const counts = new Map<string, number>()
  items.forEach((item) => {
    const key = getKey(item)
    counts.set(key, (counts.get(key) || 0) + (isCounted(item) ? 1 : 0))
  })
  return Array.from(counts, ([value, count]) => ({ value, label: value, count }))
}

export const MappingResultsTable: FC<MappingResultsTableProps> = (props) => {
  const {
    metadatas,
    results,
    correctedResults,
    onCorrectNomenclatureSelect,
    isUTDIteration,
    isSMRIteration,
    smrMetadatas,
    isIterationApproved,
    isLoading,
    status: controlledStatus,
    onStatusChange,
  } = props

  // Смета: плоская таблица с фильтрами по типу, разделу и статусу (KRE-713)
  const positionsByRow = new Map(
    (smrMetadatas?.mapped_positions || []).map((p) => [p.row_number, p]),
  )
  const getPosition = (result: WithId<MappingResult>) =>
    positionsByRow.get(result.result?.row_number ?? -1)?.position
  const getType = (result: WithId<MappingResult>) =>
    getTypeLabel(getPosition(result)?.type)
  const getSection = (result: WithId<MappingResult>) =>
    getPosition(result)?.section_name || NO_SECTION
  const correctedById = useMemo(
    () => new Map(correctedResults.map((corrected) => [corrected.result_id, corrected])),
    [correctedResults],
  )
  const isChecked = (result: WithId<MappingResult>) =>
    !!correctedById.get(result.id)?.nomenclature
  // Как у строки (MappingResultRow): у сметы без кода НСИ — тоже "нет"
  const isNotFound = (result: WithId<MappingResult>) =>
    !hasMappedNomenclature(result, isSMRIteration)
  // Как у строки: название в смете = материал НСИ (KRE-758)
  const isSameAsNsi = (result: WithId<MappingResult>) =>
    isSameAsMappedNomenclature(result, true)

  const [type, setType] = useState<string>()
  const [section, setSection] = useState(ALL_SECTIONS)
  const [localStatus, setLocalStatus] = useState<SmrStatusFilter>("all")
  const status = controlledStatus ?? localStatus
  const setStatus = onStatusChange ?? setLocalStatus
  // Смета: совпадающие с НСИ прячутся вместе с любым статусом, а не вместо него
  const [isSameAsNsiHidden, setIsSameAsNsiHidden] = useState(false)
  const isSameAsNsiFilterOn = !!isSMRIteration && isSameAsNsiHidden
  // Счётчики типа, раздела и статуса — сколько строк видно: спрятанные
  // "Скрыть совпадающие" не считаем, чтобы было видно, сколько скрылось
  const isCounted = (result: WithId<MappingResult>) =>
    !isSameAsNsiFilterOn || !isSameAsNsi(result)

  // Доступная ширина для таблицы — от неё ширины колонок (сайдбар, окно).
  // Меряем контейнер, а не саму таблицу: с fixed-раскладкой таблица не уже
  // суммы колонок, и после разворачивания сайдбара оставалась прежней — колонки
  // не пересчитывались и уезжали за край. callback-ref, а не useEffect:
  // таблица появляется позже первого рендера
  //
  // Сайдбар сворачивается с анимацией: ширина контейнера меняется каждый кадр.
  // Перекладка таблицы на 500 строк — ~30 мс, на каждом кадре интерфейс
  // дёргался. Поэтому ширина таблицы — в пикселях по последнему замеру (пока
  // она та же, кадр анимации стоит ~8 мс), а колонки пересчитываем один раз,
  // когда размер перестал меняться
  const [tableWidth, setTableWidth] = useState(0)
  const resizeObserver = useRef<ResizeObserver>()
  const resizeTimer = useRef<number>()
  const tableRef = useCallback((table: HTMLTableElement | null) => {
    resizeObserver.current?.disconnect()
    window.clearTimeout(resizeTimer.current)
    const container = table?.parentElement
    if (!container) return
    setTableWidth(container.clientWidth)
    resizeObserver.current = new ResizeObserver(() => {
      window.clearTimeout(resizeTimer.current)
      resizeTimer.current = window.setTimeout(
        () => setTableWidth(container.clientWidth),
        RESIZE_SETTLE_MS,
      )
    })
    resizeObserver.current.observe(container)
  }, [])

  // Счётчики каждого фильтра считаются с учётом фильтров левее:
  // разделы — внутри типа, статусы — внутри типа и раздела. "Скрыть
  // совпадающие" — для всех сразу (isCounted)
  const types = countBy(results, getType, isCounted)
  const activeType = type ?? types[0]?.value

  // Смета и УПД — общий вид таблицы проверки и фильтры статуса; тип и
  // раздел — только у сметы. У УПД тип и раздел у всех строк одни и те же,
  // поэтому те же расчёты ниже работают и для неё
  const isReview = isSMRIteration || isUTDIteration
  const columns: Column[] = isSMRIteration
    ? getSmrColumns(activeType, tableWidth)
    : isUTDIteration
      ? getUtdColumns(tableWidth)
      : COLUMNS_NAME.map((title) => ({ title }))
  const byType = results.filter((result) => getType(result) === activeType)
  const sections = [
    { value: ALL_SECTIONS, label: "Все разделы", count: byType.filter(isCounted).length },
    ...countBy(byType, getSection, isCounted).sort((a, b) =>
      a.label.localeCompare(b.label, "ru"),
    ),
  ]
  const bySection =
    section === ALL_SECTIONS
      ? byType
      : byType.filter((result) => getSection(result) === section)
  const countedBySection = bySection.filter(isCounted)
  const statuses: SmrFilterOption<SmrStatusFilter>[] = [
    { value: "all", label: "Все", count: countedBySection.length },
    {
      value: "unchecked",
      label: "Не проверены",
      count: countedBySection.filter((r) => !isChecked(r)).length,
    },
    {
      value: "notFound",
      label: "Нет в НСИ",
      count: countedBySection.filter(isNotFound).length,
    },
  ]
  const byStatus = bySection.filter((result) =>
    status === "unchecked"
      ? !isChecked(result)
      : status === "notFound"
        ? isNotFound(result)
        : true,
  )
  // Сколько строк прячет "Скрыть совпадающие" — внутри типа, раздела и статуса
  const sameAsNsiCount = isSMRIteration ? byStatus.filter(isSameAsNsi).length : 0
  const bySameAsNsi = isSameAsNsiFilterOn
    ? byStatus.filter((result) => !isSameAsNsi(result))
    : byStatus

  const handleTypeChange = (newType: string) => {
    setType(newType)
    setSection(ALL_SECTIONS)
  }

  const visibleResults = isReview ? bySameAsNsi : results
  // Рисуем все строки выбранного типа, а раздел и статус фильтруем CSS-ом
  // по меткам строк (data-*): переключение фильтра не перерисовывает сотни строк
  const renderedResults = isReview ? byType : results
  // Правила — только для строк позиций (у них есть data-section): строку
  // "Под эти фильтры позиций нет" прятать нельзя
  const hideRules = [
    section !== ALL_SECTIONS &&
      `#review-rows > tr[data-section]:not([data-section=${JSON.stringify(section)}])`,
    status === "unchecked" && "#review-rows > tr[data-checked='true']",
    status === "notFound" && "#review-rows > tr[data-not-found='false']",
    isSameAsNsiFilterOn && "#review-rows > tr[data-same-as-nsi='true']",
  ].filter(Boolean)
  const filterCss = hideRules.length ? `${hideRules.join(", ")} { display: none; }` : ""

  return (
    <Flex w="full" direction="column" gap={3}>
      {isSMRIteration && (
        <SMRResultsFilters
          types={types}
          soonTypes={SOON_TYPES.filter(
            (soonType) => !types.some((option) => option.value === soonType),
          )}
          type={activeType}
          onTypeChange={handleTypeChange}
          sections={sections}
          section={section}
          onSectionChange={setSection}
          statuses={statuses}
          status={status}
          onStatusChange={setStatus}
          sameAsNsiCount={sameAsNsiCount}
          sameAsNsiHint={getSmrItemWords(activeType).sameAsNsiHint}
          isSameAsNsiHidden={isSameAsNsiHidden}
          onSameAsNsiHiddenChange={setIsSameAsNsiHidden}
        />
      )}

      {isUTDIteration && (
        <SMRResultsFilters statuses={statuses} status={status} onStatusChange={setStatus} />
      )}

      {isReview && <style>{filterCss}</style>}

      <Table
        ref={tableRef}
        w={isReview && tableWidth ? `${tableWidth}px` : "full"}
        // Без зебры — со скрытыми фильтром строками она сбивается; ширины
        // колонок фиксированы, а не пересчитываются по содержимому
        variant={isReview ? "simple" : "striped"}
        sx={isReview ? { tableLayout: "fixed" } : undefined}
      >
        <Thead>
          <Tr>
            {columns.map((column, index) => (
              <Th
                key={index}
                w={column.width || "fit-content"}
                px={column.isCompact ? 2 : 3}
                py={2}
                isNumeric={column.isNumeric}
                {...(isReview && stickyHeaderCellProps)}
              >
                {column.title}
              </Th>
            ))}
          </Tr>
        </Thead>

        <Tbody id={isReview ? "review-rows" : undefined}>
          {renderedResults.map((result) => (
            <MappingResultRow
              key={result.id}
              metadatas={metadatas}
              mappingResult={result}
              correctedResult={correctedById.get(result.id)}
              onCorrectNomenclatureSelect={onCorrectNomenclatureSelect}
              isUTDIteration={isUTDIteration}
              isSMRIteration={isSMRIteration}
              smrPosition={positionsByRow.get(result.result?.row_number ?? -1)}
              isIterationApproved={isIterationApproved}
              isLoading={isLoading}
            />
          ))}

          {/* Под фильтры ничего не попало */}
          {isReview && visibleResults.length === 0 && (
            <Tr>
              <Td colSpan={columns.length} py={10} textAlign="center" color="gray.500">
                {status === "unchecked" && !isSameAsNsiFilterOn
                  ? "Все позиции проверены"
                  : byStatus.length > 0 && isSameAsNsiFilterOn
                    ? "Все позиции здесь совпадают с НСИ"
                    : "Под эти фильтры позиций нет"}
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>
    </Flex>
  )
}
